import L from 'leaflet';

import type { CemeteryData, OverlayPlacement } from '../types';

export type ReferencePointIconOption = {
	key: string;
	label: string;
	glyph: string;
	color: string;
};

export const REFERENCE_POINT_ICON_OPTIONS: ReferencePointIconOption[] = [
	{ key: 'gate', label: 'Vhod', glyph: '🚪', color: '#1769aa' },
	{ key: 'water', label: 'Voda', glyph: '💧', color: '#00838f' },
	{ key: 'church', label: 'Cerkev', glyph: '⛪', color: '#6d4c41' },
	{ key: 'urn', label: 'Žarna vežica', glyph: '⚱', color: '#8d6e63' },
	{ key: 'waste', label: 'Smetnjak', glyph: '🗑', color: '#546e7a' },
	{ key: 'parking', label: 'Parkirišče', glyph: '🅿', color: '#3949ab' },
	{ key: 'office', label: 'Pisarna', glyph: '🏢', color: '#5e35b1' },
	{ key: 'wc', label: 'WC', glyph: '🚻', color: '#455a64' },
	{ key: 'bench', label: 'Klop', glyph: '🪑', color: '#2e7d32' },
	{ key: 'flower', label: 'Cvetje', glyph: '🌸', color: '#c2185b' },
	{ key: 'cross', label: 'Križ', glyph: '✝', color: '#4e342e' },
	{ key: 'memorial', label: 'Spomenik', glyph: '🪦', color: '#6a1b9a' },
	{ key: 'fountain', label: 'Fontana', glyph: '⛲', color: '#0277bd' },
	{ key: 'info', label: 'Info', glyph: 'ℹ', color: '#00897b' },
	{ key: 'other', label: 'Drugo', glyph: '📍', color: '#616161' },
];

export const getReferencePointIconOption = (iconKey?: string): ReferencePointIconOption => {
	return REFERENCE_POINT_ICON_OPTIONS.find((option) => option.key === iconKey) ?? REFERENCE_POINT_ICON_OPTIONS[REFERENCE_POINT_ICON_OPTIONS.length - 1];
};

export const normalizeOverlayOpacity = (value: number | undefined | null, fallback = 0.8): number => {
	if (value == null || Number.isNaN(value)) {
		return fallback;
	}

	if (value <= 0) {
		return fallback;
	}

	if (value > 1) {
		return 1;
	}

	return value;
};

export const createPlacementFromBounds = (
	southwest: [number, number],
	northeast: [number, number],
	opacity = 1,
): OverlayPlacement => ({
	topLeft: { lat: northeast[0], lng: southwest[1] },
	topRight: { lat: northeast[0], lng: northeast[1] },
	bottomLeft: { lat: southwest[0], lng: southwest[1] },
	bottomRight: { lat: southwest[0], lng: northeast[1] },
	opacity: normalizeOverlayOpacity(opacity),
});

export const getOverlayPlacement = (cemeteryData: CemeteryData | null): OverlayPlacement | null => {
	if (!cemeteryData) {
		return null;
	}

	if (
		cemeteryData.overlayTopLeftLat != null &&
		cemeteryData.overlayTopLeftLng != null &&
		cemeteryData.overlayTopRightLat != null &&
		cemeteryData.overlayTopRightLng != null &&
		cemeteryData.overlayBottomLeftLat != null &&
		cemeteryData.overlayBottomLeftLng != null &&
		cemeteryData.overlayBottomRightLat != null &&
		cemeteryData.overlayBottomRightLng != null
	) {
		return {
			topLeft: { lat: cemeteryData.overlayTopLeftLat, lng: cemeteryData.overlayTopLeftLng },
			topRight: { lat: cemeteryData.overlayTopRightLat, lng: cemeteryData.overlayTopRightLng },
			bottomLeft: { lat: cemeteryData.overlayBottomLeftLat, lng: cemeteryData.overlayBottomLeftLng },
			bottomRight: { lat: cemeteryData.overlayBottomRightLat, lng: cemeteryData.overlayBottomRightLng },
			opacity: normalizeOverlayOpacity(cemeteryData.overlayOpacity),
		};
	}

	if (
		cemeteryData.southwestLat != null &&
		cemeteryData.southwestLng != null &&
		cemeteryData.northeastLat != null &&
		cemeteryData.northeastLng != null
	) {
		return createPlacementFromBounds(
			[cemeteryData.southwestLat, cemeteryData.southwestLng],
			[cemeteryData.northeastLat, cemeteryData.northeastLng],
			normalizeOverlayOpacity(cemeteryData.overlayOpacity),
		);
	}

	return null;
};

export const overlayPlacementToPayload = (placement: OverlayPlacement | null) => ({
	overlayTopLeftLat: placement?.topLeft.lat,
	overlayTopLeftLng: placement?.topLeft.lng,
	overlayTopRightLat: placement?.topRight.lat,
	overlayTopRightLng: placement?.topRight.lng,
	overlayBottomLeftLat: placement?.bottomLeft.lat,
	overlayBottomLeftLng: placement?.bottomLeft.lng,
	overlayBottomRightLat: placement?.bottomRight.lat,
	overlayBottomRightLng: placement?.bottomRight.lng,
	overlayOpacity: normalizeOverlayOpacity(placement?.opacity),
});

export const getPlacementFromOverlayInstance = (overlay: L.DistortableImageOverlay | null, fallbackOpacity = 1): OverlayPlacement | null => {
	if (!overlay) {
		return null;
	}

	const corners = overlay.getCorners();
	if (corners.length < 4) {
		return null;
	}

	return {
		topLeft: { lat: corners[0].lat, lng: corners[0].lng },
		topRight: { lat: corners[1].lat, lng: corners[1].lng },
		bottomLeft: { lat: corners[2].lat, lng: corners[2].lng },
		bottomRight: { lat: corners[3].lat, lng: corners[3].lng },
		opacity: normalizeOverlayOpacity((overlay.options.opacity as number | undefined) ?? fallbackOpacity),
	};
};

export const projectReferencePoint = (
	point: { x: number; y: number },
	placement: OverlayPlacement,
): [number, number] => {
	const u = point.x / 100;
	const v = point.y / 100;

	const lat =
		placement.topLeft.lat * (1 - u) * (1 - v) +
		placement.topRight.lat * u * (1 - v) +
		placement.bottomLeft.lat * (1 - u) * v +
		placement.bottomRight.lat * u * v;
	const lng =
		placement.topLeft.lng * (1 - u) * (1 - v) +
		placement.topRight.lng * u * (1 - v) +
		placement.bottomLeft.lng * (1 - u) * v +
		placement.bottomRight.lng * u * v;

	return [lat, lng];
};

export const unprojectReferencePoint = (
	lat: number,
	lng: number,
	placement: OverlayPlacement,
): { x: number; y: number } | null => {
	const clampUnit = (value: number): number => Math.min(1, Math.max(0, value));
	const epsilon = 1e-9;
	const horizontalLat = ((placement.topRight.lat - placement.topLeft.lat) + (placement.bottomRight.lat - placement.bottomLeft.lat)) / 2;
	const horizontalLng = ((placement.topRight.lng - placement.topLeft.lng) + (placement.bottomRight.lng - placement.bottomLeft.lng)) / 2;
	const verticalLat = ((placement.bottomLeft.lat - placement.topLeft.lat) + (placement.bottomRight.lat - placement.topRight.lat)) / 2;
	const verticalLng = ((placement.bottomLeft.lng - placement.topLeft.lng) + (placement.bottomRight.lng - placement.topRight.lng)) / 2;
	const deltaLat = lat - placement.topLeft.lat;
	const deltaLng = lng - placement.topLeft.lng;
	const affineDeterminant = horizontalLat * verticalLng - verticalLat * horizontalLng;

	let u = 0.5;
	let v = 0.5;
	if (Math.abs(affineDeterminant) > epsilon) {
		u = clampUnit((deltaLat * verticalLng - deltaLng * verticalLat) / affineDeterminant);
		v = clampUnit((deltaLng * horizontalLat - deltaLat * horizontalLng) / affineDeterminant);
	}

	for (let index = 0; index < 20; index += 1) {
		const oneMinusU = 1 - u;
		const oneMinusV = 1 - v;
		const projectedLat =
			placement.topLeft.lat * oneMinusU * oneMinusV +
			placement.topRight.lat * u * oneMinusV +
			placement.bottomLeft.lat * oneMinusU * v +
			placement.bottomRight.lat * u * v;
		const projectedLng =
			placement.topLeft.lng * oneMinusU * oneMinusV +
			placement.topRight.lng * u * oneMinusV +
			placement.bottomLeft.lng * oneMinusU * v +
			placement.bottomRight.lng * u * v;

		const latError = projectedLat - lat;
		const lngError = projectedLng - lng;
		if (Math.abs(latError) + Math.abs(lngError) < epsilon) {
			return { x: clampUnit(u) * 100, y: clampUnit(v) * 100 };
		}

		const dLatDu =
			(placement.topRight.lat - placement.topLeft.lat) * oneMinusV +
			(placement.bottomRight.lat - placement.bottomLeft.lat) * v;
		const dLatDv =
			(placement.bottomLeft.lat - placement.topLeft.lat) * oneMinusU +
			(placement.bottomRight.lat - placement.topRight.lat) * u;
		const dLngDu =
			(placement.topRight.lng - placement.topLeft.lng) * oneMinusV +
			(placement.bottomRight.lng - placement.bottomLeft.lng) * v;
		const dLngDv =
			(placement.bottomLeft.lng - placement.topLeft.lng) * oneMinusU +
			(placement.bottomRight.lng - placement.topRight.lng) * u;

		const determinant = dLatDu * dLngDv - dLatDv * dLngDu;
		if (Math.abs(determinant) < epsilon) {
			break;
		}

		const deltaU = (dLngDv * latError - dLatDv * lngError) / determinant;
		const deltaV = (-dLngDu * latError + dLatDu * lngError) / determinant;

		u = clampUnit(u - deltaU);
		v = clampUnit(v - deltaV);
	}

	const x = clampUnit(u) * 100;
	const y = clampUnit(v) * 100;
	const projected = projectReferencePoint({ x, y }, placement);
	const distance = Math.abs(projected[0] - lat) + Math.abs(projected[1] - lng);

	return distance < 1e-7 ? { x, y } : null;
};
